/**
 * Entry point: boot the app into the main menu, then transition into an
 * in-game session when the player clicks New Game. v2M1 introduced this
 * shell; prior to v2M1 the entry point booted directly into gameplay.
 *
 * Lifecycle model (UI_UX §9.3):
 *   App start ──▶ Main menu
 *                   ├──▶ New Game ──▶ [setup ──▶] InGame
 *                   ├──▶ Load Game ── stubbed (v2M2)
 *                   ├──▶ Settings  ── stubbed (v2M4)
 *                   └──▶ Exit      ── confirm
 *
 *   InGame ─── (Return to Main Menu) ──▶ Main menu
 *
 * Canvas + LoadedGameData live for the app's full lifetime; the session
 * (sim loop, render loop, UI mounts) is torn down and recreated on each
 * Main Menu round-trip.
 */

import { loadGameData, type LoadedGameData } from './data/loader';
import { createInitialGameState, type NewGameOptions } from './game/scenario';
import { createSaveStorage, type SaveStorage } from './save/storage';
import {
  createSettingsStorage,
  type SettingsStorage,
} from './settings/storage';
import type { Settings } from './settings/types';
import type { GameState } from './state/types';
import { mountMainMenu, type MainMenuHandle } from './ui/main-menu';
import {
  mountNewGameSetup,
  type NewGameSetupHandle,
} from './ui/new-game-setup';
import {
  mountLoadSlotPicker,
  type LoadSlotPickerHandle,
} from './ui/load-slot-picker';
import {
  mountSaveSlotPicker,
  type SaveSlotPickerHandle,
} from './ui/save-slot-picker';
import {
  mountSettingsOverlay,
  type SettingsOverlayHandle,
} from './ui/settings-overlay';
import { startGameSession, type SessionHandle } from './ui/session';
import {
  getAudioEngine,
  loadMissileSamples,
  registerAlertCues,
  registerColonyCues,
  registerMusicCues,
  registerShipCues,
  registerStructureCues,
  registerUiCues,
  registerWeaponCues,
} from './audio';

type AppMode =
  | { kind: 'main-menu'; handle: MainMenuHandle }
  | { kind: 'new-game-setup'; handle: NewGameSetupHandle }
  | { kind: 'in-game'; session: SessionHandle };

interface AppContext {
  canvas: HTMLCanvasElement;
  data: LoadedGameData;
  storage: SaveStorage;
  settingsStorage: SettingsStorage;
  /**
   * Live Settings — replaced wholesale by `enterSettings`'s onSave path
   * so any caller using `getSettings()` (e.g. the speed controller seed
   * or the in-game-menu confirm gates) reads fresh values immediately.
   */
  settings: Settings;
  /** Save-picker handle — non-null only while a Save picker is mounted on top of the in-game menu. */
  savePicker: SaveSlotPickerHandle | null;
  /** Load-picker handle — non-null only while a Load picker is mounted on top of the main menu. */
  loadPicker: LoadSlotPickerHandle | null;
  /** Settings-overlay handle — non-null only while the Settings overlay is mounted (over either menu). */
  settingsOverlay: SettingsOverlayHandle | null;
  mode: AppMode | null;
}

function bootstrap(): void {
  try {
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement | null;
    if (!canvas) throw new Error('Expected <canvas id="game-canvas"> in DOM');
    const data = loadGameData();
    const storage = createSaveStorage();
    const settingsStorage = createSettingsStorage();
    const settings = settingsStorage.load();

    const ctx: AppContext = {
      canvas,
      data,
      storage,
      settingsStorage,
      settings,
      savePicker: null,
      loadPicker: null,
      settingsOverlay: null,
      mode: null,
    };

    // On window unload, tear down whichever mode is active so the render/sim
    // loops stop cleanly. Browsers will reap anyway, but a clean stop helps
    // when hot-reloading in dev.
    window.addEventListener('beforeunload', () => {
      teardownCurrent(ctx);
    });

    // v2M8.1 autoplay gating — browsers start AudioContext in 'suspended'
    // state until a user gesture. Boot the engine here (allocates the
    // context but doesn't make sound yet); resume on first click / keydown
    // so the main-menu buttons or any hotkey unlocks audio. Listener
    // self-removes after the first call.
    const audio = getAudioEngine();
    registerUiCues(audio);
    registerAlertCues(audio);
    registerWeaponCues(audio);
    registerShipCues(audio);
    registerStructureCues(audio);
    registerMusicCues(audio);
    registerColonyCues(audio);
    // v3M28.6 — race-replace the four procedural missile cues with
    // the commissioned ElevenLabs samples. Fire-and-forget: procedural
    // fallbacks stay active until each decode resolves (typically a
    // few hundred ms post-boot, well before the first missile fires).
    void loadMissileSamples(audio);
    // v2M8.8 — seed bus volumes from persisted settings. Engine
    // already has sensible defaults pre-load, so a fresh-install
    // first-run isn't deafening; this just overrides with whatever
    // the player saved.
    audio.applyVolumes(settings.audio);
    const unlockAudio = (): void => {
      audio.tryResume();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    // v2M8.2 UI SFX wiring — global delegated listeners for `<button>`
    // click + hover. Skips disabled buttons and any element tagged with
    // `data-no-sound`. Per-overlay panel-open / panel-close cues fire
    // from the individual mount* functions (in-game-menu.ts,
    // settings-overlay.ts, save-slot-picker.ts, load-slot-picker.ts,
    // research-panel.ts). HUD / detail-panel / mini-galaxy stay quiet —
    // they're persistent UI, not button-driven panels.
    document.addEventListener('pointerdown', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('button');
      if (!btn || btn.disabled) return;
      if ((btn as HTMLElement).dataset.noSound !== undefined) return;
      audio.playCue('ui:click');
    });
    // MFix5.1 — dedupe hover SFX so a stationary cursor over one button
    // doesn't replay the cue every render tick. Two contributing causes:
    //   1. `pointerover` bubbles, firing on each child-boundary crossing
    //      within a button (text nodes, label spans).
    //   2. Detail-panel rebuilds its button DOM every tick (~100 ms),
    //      generating fake pointerout/over cycles under a stationary
    //      cursor as the underlying element gets replaced.
    // Dedupe by `textContent` (stable across rebuilds for the same
    // logical button) and clear only when the cursor moves to a
    // non-button area (`pointermove` outside any <button>).
    let lastHoverKey: string | null = null;
    document.addEventListener('pointerover', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('button');
      if (!btn || btn.disabled) return;
      if ((btn as HTMLElement).dataset.noSound !== undefined) return;
      const key = btn.textContent ?? '';
      if (key === lastHoverKey) return;
      lastHoverKey = key;
      audio.playCue('ui:hover');
    });
    document.addEventListener('pointermove', (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.('button');
      if (!btn) lastHoverKey = null;
    });

    // v2M8.8 — M-hotkey master mute (AUDIO §11). Skips if a text
    // input has focus so it doesn't swallow letter keys mid-typing.
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'm' && e.key !== 'M') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      audio.toggleMute();
    });

    // v2M8.8 — focus-loss fade (AUDIO §11). Master gain ramps to 0
    // over 200 ms when the tab loses focus and back to slider value
    // on focus return. Slider value itself is unaffected.
    window.addEventListener('blur', () => {
      audio.setFocusLost(true);
    });
    window.addEventListener('focus', () => {
      audio.setFocusLost(false);
    });

    enterMainMenu(ctx);
  } catch (err) {
    showBootstrapError(err);
    throw err;
  }
}

function enterMainMenu(ctx: AppContext): void {
  teardownCurrent(ctx);
  const handle = mountMainMenu(document.body, {
    onNewGame: () => enterNewGameSetup(ctx),
    onLoadGame: () => openLoadPicker(ctx),
    onSettings: () => openSettings(ctx),
    onExit: () => {
      if (ctx.settings.gameplay.confirmExit && !confirm('Exit Strategos Universalis?')) {
        return;
      }
      teardownCurrent(ctx);
      // Browser can't close the tab programmatically; show a neutral
      // goodbye and leave the blank canvas. Tauri builds would call
      // window.close() here.
      const body = document.body;
      body.innerHTML =
        '<div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#05060A;color:#D7DEEA;font-family:Inter,sans-serif;font-size:16px;letter-spacing:0.06em;">Thanks for playing.</div>';
    },
  });
  ctx.mode = { kind: 'main-menu', handle };
}

function enterNewGameSetup(ctx: AppContext): void {
  teardownCurrent(ctx);
  const handle = mountNewGameSetup(document.body, ctx.data, {
    onStart: (options: NewGameOptions) => {
      const state = createInitialGameState(ctx.data, options);
      enterInGame(ctx, state);
    },
    onBack: () => enterMainMenu(ctx),
  });
  ctx.mode = { kind: 'new-game-setup', handle };
}

function enterInGame(ctx: AppContext, state: GameState): void {
  teardownCurrent(ctx);
  const session = startGameSession({
    canvas: ctx.canvas,
    data: ctx.data,
    state,
    onReturnToMainMenu: () => enterMainMenu(ctx),
    onExit: () => {
      // Session has already applied the v2M4 confirmExit gate before
      // calling here, so this path just performs the exit.
      teardownCurrent(ctx);
      document.body.innerHTML =
        '<div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#05060A;color:#D7DEEA;font-family:Inter,sans-serif;font-size:16px;letter-spacing:0.06em;">Thanks for playing.</div>';
    },
    onSave: (getState) => openSavePicker(ctx, getState),
    onSettings: () => openSettings(ctx),
    getSettings: () => ctx.settings,
  });
  ctx.mode = { kind: 'in-game', session };
}

/**
 * Mount the save slot picker on top of the in-game menu. Sim is already
 * paused (the menu paused it); picker closes back to the menu on save
 * or cancel — sim resume is the player's call via Resume.
 */
function openSavePicker(ctx: AppContext, getState: () => GameState): void {
  if (ctx.savePicker !== null) return; // already open
  const close = (): void => {
    ctx.savePicker?.destroy();
    ctx.savePicker = null;
  };
  ctx.savePicker = mountSaveSlotPicker(document.body, {
    storage: ctx.storage,
    getState,
    onSaved: () => {
      close();
    },
    onCancel: close,
  });
}

/**
 * Mount the Settings overlay (v2M4). Reachable from both the main menu
 * and the in-game menu — the overlay is z-1100 and sits cleanly on top
 * of either. On Save: persist via SettingsStorage and update ctx.settings
 * so the next caller of `getSettings` sees the new values immediately.
 * On Cancel: close, no persistence.
 */
function openSettings(ctx: AppContext): void {
  if (ctx.settingsOverlay !== null) return;
  const close = (): void => {
    ctx.settingsOverlay?.destroy();
    ctx.settingsOverlay = null;
  };
  ctx.settingsOverlay = mountSettingsOverlay(document.body, {
    current: ctx.settings,
    onSave: (next) => {
      ctx.settings = next;
      ctx.settingsStorage.save(next);
      // v2M8.8 — push the latest audio volumes into the engine so the
      // change takes effect immediately without waiting for next session.
      getAudioEngine().applyVolumes(next.audio);
      close();
    },
    onCancel: close,
  });
}

/**
 * Mount the load slot picker on top of the main menu. On Load → restore
 * the deserialised state into a new in-game session, replacing the main
 * menu. On Cancel → close the picker, leave the main menu visible.
 */
function openLoadPicker(ctx: AppContext): void {
  if (ctx.loadPicker !== null) return;
  const close = (): void => {
    ctx.loadPicker?.destroy();
    ctx.loadPicker = null;
  };
  ctx.loadPicker = mountLoadSlotPicker(document.body, {
    storage: ctx.storage,
    data: ctx.data,
    onLoaded: (state) => {
      close();
      enterInGame(ctx, state);
    },
    onCancel: close,
  });
}

function teardownCurrent(ctx: AppContext): void {
  // Close any picker / overlay that's mounted before tearing down the
  // mode beneath it — keeps overlay z-order tidy on transitions.
  if (ctx.savePicker !== null) {
    ctx.savePicker.destroy();
    ctx.savePicker = null;
  }
  if (ctx.loadPicker !== null) {
    ctx.loadPicker.destroy();
    ctx.loadPicker = null;
  }
  if (ctx.settingsOverlay !== null) {
    ctx.settingsOverlay.destroy();
    ctx.settingsOverlay = null;
  }
  if (!ctx.mode) return;
  switch (ctx.mode.kind) {
    case 'main-menu':
      ctx.mode.handle.destroy();
      break;
    case 'new-game-setup':
      ctx.mode.handle.destroy();
      break;
    case 'in-game':
      ctx.mode.session.destroy();
      break;
  }
  ctx.mode = null;
}

function showBootstrapError(err: unknown): void {
  const el = document.getElementById('bootstrap-error');
  if (!el) return;
  const msg = err instanceof Error ? `${err.message}\n${err.stack ?? ''}` : String(err);
  el.textContent = `Bootstrap error: ${msg}`;
  el.style.display = 'flex';
}

bootstrap();
